import { AppState } from '../../app/types';
import { captureEvent } from '../../gateways/eventCaptureClient';
import googleAnalyticsClient from '../../gateways/googleAnalyticsClient';
import * as clickButton from './events/clickButton';
import * as clickLink from './events/clickLink';
import * as elementInteracted from './events/elementInteracted';
import { AnalyticsEvent } from './events/event';
import * as highlightingCreateNote from './events/highlighting/createNote';
import * as deleteHighlight from './events/highlighting/delete';
import * as highlightingEditColor from './events/highlighting/editColor';
import * as highlightingEditAnnotation from './events/highlighting/editNote';
import * as showCreate from './events/highlighting/showCreate';
import * as showHelpInfo from './events/highlighting/showHelpInfo';
import * as showLogin from './events/highlighting/showLogin';
import * as openCloseMH from './events/highlighting/summaryPopup/openClose';
import * as openCloseKeyboardShortcuts from './events/keyboardShortcuts/openClosePopUp';
import * as openNudgeStudyTools from './events/openNudgeStudyTools';
import * as pageFocus from './events/pageFocus';
import * as openClosePracticeQuestions from './events/practiceQuestions/openClosePopUp';
import * as print from './events/print';
import * as search from './events/search';
import * as sessionStarted from './events/sessionStarted';
import * as closeStudyGuides from './events/studyGuides/closePopUp';
import * as openStudyGuides from './events/studyGuides/openPopUp';
import * as openUTG from './events/studyGuides/openUTG';
import * as unload from './events/unload';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventConstructor<Args extends any[] = any[]> = (...args: Args) => (AnalyticsEvent | void);
type Selector = (state: AppState) => object;
interface Event {track: EventConstructor; selector: Selector; }

const triggerEvent = <E extends Event>(event: E): E['track'] => (...args) => {
  const analyticsEvent = event.track(...args);

  if (analyticsEvent && analyticsEvent.getGoogleAnalyticsPayload) {
    googleAnalyticsClient.trackEventPayload(analyticsEvent.getGoogleAnalyticsPayload());
  }
  if (analyticsEvent && analyticsEvent.getEventCapturePayload) {
    captureEvent(analyticsEvent.getEventCapturePayload());
  }
};

const bindTrackSelector = <E extends Event>(event: E, track: E['track']) => (state: AppState | (() => AppState)) =>  {
  type RemainingArgumentTypes = E['track'] extends (
    d: ReturnType<E['selector']>,
    ...args: infer A
  ) => unknown
    ? A
    : never;

  return (...args: RemainingArgumentTypes) => {
    const data = event.selector(typeof state === 'function' ? state() : state);
    track(data, ...args);
  };
};

export const mapEventType = <E extends Event>(event: E) => {
  const track = triggerEvent(event);

  return {
    ...event,
    bind: bindTrackSelector(event, track),
    track,
  };
};

export const events = {
  clickButton: mapEventType(clickButton),
  clickLink: mapEventType(clickLink),
  closeStudyGuides: mapEventType(closeStudyGuides),
  createNote: mapEventType(highlightingCreateNote),
  deleteHighlight: mapEventType(deleteHighlight),
  editAnnotation: mapEventType(highlightingEditAnnotation),
  editNoteColor: mapEventType(highlightingEditColor),
  elementInteracted: mapEventType(elementInteracted),
  openCloseKeyboardShortcuts: mapEventType(openCloseKeyboardShortcuts),
  openCloseMH: mapEventType(openCloseMH),
  openClosePracticeQuestions: mapEventType(openClosePracticeQuestions),
  openNudgeStudyTools: mapEventType(openNudgeStudyTools),
  openStudyGuides: mapEventType(openStudyGuides),
  openUTG: mapEventType(openUTG),
  pageFocus: mapEventType(pageFocus),
  print: mapEventType(print),
  search: mapEventType(search),
  sessionStarted: mapEventType(sessionStarted),
  showCreate: mapEventType(showCreate),
  showHelpInfo: mapEventType(showHelpInfo),
  showLogin: mapEventType(showLogin),
  unload: mapEventType(unload),
};
